import { type FindingDefinitionDto, useFindingDefinition } from "@repo/data-store";
import { createFileRoute } from "@tanstack/react-router";
import { CatalogDefinition, FindingRead } from "@/features/catalog";
import { useDefinitionChangePoints, useDefinitionView } from "@/features/catalog/hooks/use-definition-as-of";
import { asOfSearchSchema } from "@/features/catalog/lib/as-of-search";
import { findingDefinitionUrlFromRouteId } from "@/features/catalog/lib/finding-definition-route";

export const Route = createFileRoute("/catalog/findings/$findingId")({
  component: FindingDetailComponent,
  validateSearch: asOfSearchSchema,
});

function FindingDetailComponent() {
  const { findingId } = Route.useParams();
  const { asOf } = Route.useSearch();
  const { permissions } = Route.useRouteContext();
  const definitionUrl = findingDefinitionUrlFromRouteId(findingId);
  const live = useFindingDefinition(asOf ? undefined : definitionUrl);
  const view = useDefinitionView<FindingDefinitionDto>(definitionUrl, asOf, live);
  const { changePoints } = useDefinitionChangePoints(definitionUrl);

  if (view.isPending) return <CatalogDefinition.Pending />;
  if (!view.definition) {
    return <CatalogDefinition.NotFound label="Finding" backTo="/catalog/exams" backLabel="Back to catalog" />;
  }

  return (
    <FindingRead
      key={`${findingId}|${asOf ?? "latest"}`}
      finding={view.definition}
      asOf={asOf}
      changePoints={changePoints}
      // A past revision is a record, not a draft. Writing to one would either
      // resurrect it as the current revision or fail confusingly, so the whole
      // write surface is off — separately from whether the user may write at all.
      canEdit={!view.isPast && permissions.canWrite("ObservationDefinition")}
    />
  );
}
